'use client';
import type { MainContentProps } from './data';

import React from 'react';
import { IconButton } from '@chakra-ui/react';
import parse from 'html-react-parser';
import { usePathname } from 'next/navigation';

import ContentBankLink from '../ContentBankLink';
import ImagesOverview from '../ImagesOverview';
import LikeButton from '../LikeButton';
import ProductCounter from '../ProductCounter';
import SelectVariant from '../SelectVariant';
import StickyFooter from '../StickyFooter';

import {
  Box,
  Breadcrumb,
  Container,
  Divider,
  Icon,
  ListItem,
  Stack,
  Text,
  UnorderedList,
} from '@/components';
import {
  useIsMobile,
  useProductGroup,
  useProfile,
  useShare,
  useTranslation,
} from '@/hooks';
import { routes } from '@/statics';
import Like from '@/svgs/products/like.svg';
import Share from '@/svgs/products/share.svg';
import { fonts } from '@/theme';
import { getStaticRatingPercent, separator, toToman } from '@/utils';
import {
  googleEventShare,
  googleEventViewItem,
} from '@/utils/googleAnalyticsEvent';

const MainContent: MainContentProps = ({ productData, userImages }) => {
  const { t } = useTranslation('pages.products');
  const { data: profile } = useProfile();
  const handleShare = useShare();
  const pathname = usePathname();
  const [selectedVariantCode, setSelectedVariantCode] = React.useState<
    number | null
  >(null);
  const [isVariantInvalid, setIsVariantInvalid] = React.useState(false);

  const { data: selectedVariant, isLoading: loadingVariant } = useProductGroup({
    code: selectedVariantCode,
  });

  const selectVariantDesktopRef = React.useRef<HTMLDivElement | null>(null);
  const selectVariantMobileRef = React.useRef<HTMLDivElement | null>(null);
  const isMobile = useIsMobile();

  const handleNotSelectedVariant = () => {
    const ref = isMobile ? selectVariantMobileRef : selectVariantDesktopRef;

    setIsVariantInvalid(true);
    setTimeout(() => {
      ref.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'start',
      });
    }, 10);
  };

  const handleSelectVariant = (value: number) => {
    setSelectedVariantCode(value);
    setIsVariantInvalid(false);
  };

  React.useEffect(() => {
    if (productData) {
      googleEventViewItem(productData);
    }
  }, [productData]);

  return (
    <Box position="relative">
      <Container>
        <Breadcrumb
          mb={{ base: '4', md: '60px' }}
          list={[
            { title: t('home'), href: routes.home.url },
            {
              title: routes.categories.title,
              href: routes.categories.url,
            },
            {
              title: productData.categories[0].parent?.title,
              href: routes.category.href(
                productData.categories[0].parent?.code
              ),
            },
            {
              title: productData.categories[0].title,
              href: routes.subCategory.href(
                productData.categories[0].parent?.code,
                productData.categories[0]?.code
              ),
            },
            {
              title: productData?.title,
              href: routes.product.href(productData?.code),
            },
          ]}
        />
        <Stack
          direction={{ base: 'column', md: 'row' }}
          gap={{ base: '7', md: '40px' }}
          flexWrap={{ base: 'wrap', xl: 'nowrap' }}
          justify={{ base: 'center', xl: 'space-between' }}
        >
          <ImagesOverview
            productId={productData.id}
            isOutOfStock={
              !(
                productData.inventory.is_available ||
                productData.type === 'view'
              )
            }
            discounts={(productData.pricing?.discounts || []).filter(
              (discount) => discount.can_show
            )}
            galleryImages={productData.gallery_images.map((item) => item.image)}
            homeImage={{
              home_page_image: productData.home_page_image,
              alt_home_page_image: productData.alt_home_page_image,
            }}
            userImages={userImages}
            code={productData.code}
            userFavoriteProductId={productData.user_favorite_product_id}
            title={productData.title}
            price={
              productData.pricing
                ? productData.pricing.payable_price
                : productData.price
            }
            discountPrice={
              productData.pricing ? productData.pricing.discount_price : 0
            }
          />
          <Stack
            flexDir="column"
            flexWrap={{ base: 'wrap', xl: 'nowrap' }}
            flexGrow={1}
            width="100%"
          >
            <Box display={{ base: 'block', md: 'none' }}>
              <ContentBankLink
                content_bank_link={productData.content_bank_link}
                code={productData.code}
              />
            </Box>
            <Stack
              direction="row"
              gap="2"
              justify="center"
              align="center"
              fontWeight="bold"
              pb={8}
              pt={4}
            >
              <Text fontSize="md" minWidth="max-content" color="black">
                {productData.categories[0].title}
              </Text>
              <Divider borderColor="gray.600" orientation="horizontal" />
            </Stack>
            <Stack direction="row" justify="space-between" align="flex-start">
              <Text
                fontSize={{ base: '2xl', md: '5xl' }}
                fontWeight={700}
                mb={{ base: '7', md: '6' }}
              >
                {productData.title}
              </Text>
              <Stack
                direction="column"
                gap="5"
                display={{ base: 'none', xl: 'flex' }}
              >
                {profile && (
                  <LikeButton
                    code={productData.code}
                    userFavoriteProductId={productData.user_favorite_product_id}
                    itemId={productData.id}
                    title={productData.title}
                    discountPrice={
                      productData.pricing
                        ? productData.pricing.discount_price
                        : 0
                    }
                    price={
                      productData.pricing
                        ? productData.pricing.payable_price
                        : productData.price
                    }
                  />
                )}
                <IconButton
                  variant="none"
                  border="none"
                  aria-label="share"
                  icon={
                    <Icon
                      as={Share}
                      width={{ base: '16px', md: '26px' }}
                      height={{ base: '14px', md: '23px' }}
                      strokeWidth={2}
                      onClick={() => {
                        googleEventShare(productData.id);
                        void handleShare({
                          title: productData.title,
                          url: `${process.env.NEXT_PUBLIC_CLIENT_URL}${pathname}`,
                        });
                      }}
                    />
                  }
                />
              </Stack>
            </Stack>
            <Stack
              direction="row"
              gap={4}
              flexWrap={{ base: 'wrap', xl: 'nowrap' }}
              flexGrow={1}
              width="100%"
            >
              <Stack direction="column" flexGrow={1}>
                <Stack
                  direction="row"
                  justify="space-between"
                  align="center"
                  mt={{ base: 0, md: 10 }}
                  mb={{ base: 4, md: 8 }}
                >
                  <Stack direction="row" gap={3}>
                    <Icon
                      as={Like}
                      width={{ base: '18px', md: '24px' }}
                      height={{ base: '18px', md: '24px' }}
                      marginTop={{ base: '1px', md: -1 }}
                    />
                    <Text
                      fontSize={{ base: 'xs', md: 'md' }}
                      fontWeight={500}
                      color="gray.900"
                      lineHeight="27px"
                    >
                      {`${t('customersRecommended', { percent: getStaticRatingPercent(productData.code) })}`}
                    </Text>
                  </Stack>
                  {(productData.inventory.is_available ||
                    productData.type === 'view') && (
                    <Stack
                      direction="row"
                      align="center"
                      gap={4}
                      display={{ base: 'none', md: 'flex' }}
                    >
                      {productData.pricing &&
                        productData.pricing.payable_price !==
                          productData.price && (
                          <Stack direction="row" align="center" gap={1}>
                            <Text
                              color="primary.100"
                              fontWeight="bold"
                              textDecoration="line-through"
                              fontSize="xl"
                            >
                              {toToman(productData.price, {
                                labelLess: true,
                              })}
                            </Text>
                            <Text
                              color="primary.100"
                              textDecoration="line-through"
                              fontSize="sm"
                              mt={1}
                            >
                              {t('toman')}
                            </Text>
                          </Stack>
                        )}
                      <Stack direction="row" align="center" gap={1}>
                        <Text
                          color="gray.900"
                          fontSize="34px"
                          fontWeight="bold"
                        >
                          {toToman(
                            productData.pricing
                              ? productData.pricing.payable_price
                              : productData.price,
                            { labelLess: true }
                          )}
                        </Text>
                        <Text color="gray.900" fontSize="md" mt={2}>
                          {t('toman')}
                        </Text>
                      </Stack>
                    </Stack>
                  )}
                </Stack>
                {productData.type === 'view' && (
                  <Box
                    display={{ base: 'flex', md: 'none' }}
                    mb={{ base: 4, md: 8 }}
                    ref={selectVariantMobileRef}
                  >
                    <SelectVariant
                      handleSelectProduct={handleSelectVariant}
                      error={
                        isVariantInvalid ? t('pleaseSelectProduct') : undefined
                      }
                      productData={productData}
                    />
                  </Box>
                )}
                <Stack direction="row" mb={{ base: 4, md: 8 }} gap={2}>
                  <Text
                    color="gray.900"
                    fontSize={{ base: 'xs', md: 'md' }}
                  >{`${t('productCode')} : ${productData.product_code}`}</Text>
                  {profile?.user_type === 'agent' && productData.pricing && (
                    <>
                      <Divider orientation="vertical" borderColor="gray.900" />
                      <Text
                        fontSize={{ base: 'xs', md: 'md' }}
                        color="gray.900"
                      >{`${separator(productData.pricing?.cv)} : CV`}</Text>
                      <Divider orientation="vertical" borderColor="gray.900" />
                      <Text
                        fontSize={{ base: 'xs', md: 'md' }}
                        color="gray.900"
                      >{`${separator(productData.pricing?.pv)} : PV`}</Text>
                    </>
                  )}
                </Stack>
                {(productData.inventory.is_available ||
                  productData.type === 'view') && (
                  <Stack direction="row" mb={5}>
                    <Text
                      color="gray.900"
                      fontSize={{ base: 'xs', md: 'md' }}
                    >{`${t('consumerPrice')} : ${toToman(productData.price)}`}</Text>
                    <Divider orientation="vertical" borderColor="gray.900" />
                    <Text
                      color="gray.900"
                      fontSize={{ base: 'xs', md: 'md' }}
                    >{`${t('productionPrice')} : ${toToman(productData.production_price)}`}</Text>
                  </Stack>
                )}
                {productData.features.length > 0 && (
                  <Stack mr={{ base: 2, md: 4 }}>
                    <UnorderedList
                      color="gray.900"
                      sx={{
                        li: {
                          fontSize: { base: 'xs', md: 'md' },
                        },
                      }}
                    >
                      {productData.features.map((feature) => (
                        <ListItem
                          sx={{
                            '*': {
                              fontFamily: `${fonts.style.fontFamily} !important`,
                            },
                          }}
                          fontSize={{ base: 'xs', md: 'md' }}
                          key={feature}
                          mb={4}
                        >
                          {parse(feature)}
                        </ListItem>
                      ))}
                    </UnorderedList>
                  </Stack>
                )}
                {productData.type === 'view' && (
                  <Box
                    display={{ base: 'none', md: 'flex' }}
                    mb={{ base: 4, md: 8 }}
                    ref={selectVariantDesktopRef}
                  >
                    <SelectVariant
                      handleSelectProduct={handleSelectVariant}
                      error={
                        isVariantInvalid ? t('pleaseSelectProduct') : undefined
                      }
                      productData={productData}
                    />
                  </Box>
                )}
                <Stack
                  direction="row"
                  gap="4"
                  display={{ base: 'none', md: 'flex' }}
                  justify={{ md: 'flex-end', xl: 'flex-start' }}
                  mt={{ md: '7', xl: 'auto' }}
                >
                  <ContentBankLink
                    content_bank_link={productData.content_bank_link}
                    code={productData.code}
                  />
                  <ProductCounter
                    selectedVariantCode={selectedVariantCode}
                    onNotSelectedVariant={handleNotSelectedVariant}
                    isLoading={loadingVariant}
                    product={{
                      ...(selectedVariant || productData),
                      category_codes: productData.categories.map(
                        (item) => item.code
                      ),
                    }}
                  />
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Container>
      <StickyFooter
        selectedVariantCode={selectedVariantCode}
        onNotSelectedVariant={handleNotSelectedVariant}
        isLoading={loadingVariant}
        product={{
          ...(selectedVariant || productData),
          category_codes: productData.categories.map((item) => item.code),
        }}
      />
    </Box>
  );
};
export default MainContent;
